import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Spinner, Table, SortableTh, StatusBadge, EmptyValue, EXPAND_COL_WIDTH, IdLink, IdText, RowExpandToggle, TableScroll, ClampText } from '@/components/ui';
import { Task } from '../../../types/Task';
import { Project } from '../../../types/Project';
import { Effort } from '../../../types/Effort';
import TaskEffortsTable from '../../../components/TaskEffortsTable';
import InfoTooltip from '../../../components/InfoTooltip';
import { useResizableColumns } from '../../../hooks/useResizableColumns';
import { ResizeHandle } from '../../../components/ResizeHandle';
import { TaskActionsDropdown } from './ProjectTasksTab';

/** The kebab plus cell padding (W-M8). The expander has its own leading column (V3-025 / ACT-04). */
export const PROJECT_WORKS_ACTIONS_COL_WIDTH = '3.5rem';

interface ProjectWorksTableProps {
  tasks: Task[];
  loading?: boolean;
  canEditTask: boolean;
  canCloneTask: boolean;
  canCloseTask: boolean;
  canReopenTask: boolean;
  canCancelTask: boolean;
  canToggleVehicleFuel: boolean;
  onViewHistory: (task: Task) => void;
  onEditTask: (task: Task) => void;
  onCloneTask: (task: Task) => void;
  onCloseTask: (task: Task) => void;
  onReopenTask: (task: Task) => void;
  onCancelTask: (task: Task) => void;
  onPrintPDF: (task: Task) => void;
  onConvertToQuantified?: (task: Task) => void;
  onConvertToCustom?: (task: Task) => void;
  onReportAccomplishment?: (task: Task) => void;
  onSetInternalReview?: (task: Task) => void;
  onSetActive?: (task: Task) => void;
  onVehicleFuelEffortPatched?: (effort: Effort) => void;
  /** When set, clicking a task row (or task number) opens the task detail modal */
  onTaskClick?: (task: Task) => void;
  /**
   * Controlled (server-side) sorting. Pass all three to have the parent own the sort and refetch
   * — required whenever `tasks` is one page of a server-paginated list, because sorting locally
   * would only reorder the rows currently on screen. Omit them for a fully client-side list.
   */
  sortField?: string | null;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
}

const ProjectWorksTable: React.FC<ProjectWorksTableProps> = ({
  tasks,
  loading = false,
  canEditTask,
  canCloneTask,
  canCloseTask,
  canReopenTask,
  canCancelTask,
  canToggleVehicleFuel,
  onViewHistory,
  onEditTask,
  onCloneTask,
  onCloseTask,
  onReopenTask,
  onCancelTask,
  onPrintPDF,
  onConvertToQuantified,
  onConvertToCustom,
  onReportAccomplishment,
  onSetInternalReview,
  onSetActive,
  onVehicleFuelEffortPatched,
  onTaskClick,
  sortField: controlledSortField,
  sortDirection: controlledSortDirection,
  onSort: controlledOnSort
}) => {
  const handleRowClick = (task: Task, event: React.MouseEvent) => {
    if (!onTaskClick) return;
    const target = event.target as HTMLElement;
    if (target.closest('button, a, input, .dropdown-menu, .dropdown-item')) return;
    onTaskClick(task);
  };

  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const isServerSorted = !!controlledOnSort;
  const [localSortField, setLocalSortField] = useState<string | null>('id');
  const [localSortDirection, setLocalSortDirection] = useState<'asc' | 'desc'>('desc');
  const sortField = isServerSorted ? controlledSortField ?? null : localSortField;
  const sortDirection = isServerSorted ? controlledSortDirection ?? 'desc' : localSortDirection;

  const RESIZABLE_COLUMN_IDS = [
    'taskNumber',
    'name',
    'project',
    'status',
    'type',
    'coding',
    'activity'
  ];
  const defaultWidths: Record<string, string> = {
    project: '200px',
    taskNumber: '120px',
    // Long text: room for a readable 2-line clamp (W-iP11).
    name: '300px',
    // Wide enough for the longest badge ("Active - Marked as Complete").
    status: '150px',
    type: '110px',
    coding: '120px',
    activity: '200px'
  };
  const { getColumnWidth, getColumnWidthPx, startResize } = useResizableColumns(
    RESIZABLE_COLUMN_IDS,
    { defaultWidths }
  );

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  // The synced "sticky scrollbar" strip that sat ABOVE the header row is gone (W-iP11): the
  // table's own scroll box (TableScroll) scrolls on both axes, so its horizontal scrollbar is at the
  // bottom of the table.

  // SortableTh computes the next direction itself (toggle on the active column, else asc) and
  // hands it back as `dir` — this just routes it to whichever sort is currently in charge.
  const handleSort = (field: string, dir: 'asc' | 'desc') => {
    if (isServerSorted) {
      controlledOnSort!(field, dir);
      return;
    }
    setLocalSortField(field);
    setLocalSortDirection(dir);
  };

  const sortedTasks = React.useMemo(() => {
    // Already ordered by the server across the whole result set — re-sorting the current page
    // here would be a no-op at best and, on ties, would fight the server's ordering.
    if (isServerSorted) return tasks;
    if (!sortField) return tasks;
    return [...tasks].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';
      switch (sortField) {
        case 'project':
          aValue = a.project?.projectNumber || '';
          bValue = b.project?.projectNumber || '';
          break;
        case 'taskNumber':
          aValue = a.taskNumber || '';
          bValue = b.taskNumber || '';
          break;
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'type':
          aValue = a.type || '';
          bValue = b.type || '';
          break;
        default:
          aValue = a.id;
          bValue = b.id;
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [tasks, sortField, sortDirection, isServerSorted]);

  const toggleEfforts = (taskId: number) => {
    setExpandedRows((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const projectMarkups = (project?: Project) =>
    project
      ? {
          vehicleMarkup: project.vehicleMarkup,
          materialMarkup: project.materialMarkup,
          labourMarkup: project.labourMarkup,
          subcontractorMarkup: project.subcontractorMarkup
        }
      : undefined;

  // The table box is always rendered (a spinner sits over it while loading) so the list region
  // never collapses and re-expands between fetches.
  return (
    <div className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col">
      {/* Mobile (< md): stacked cards — the wide task table doesn't fit a phone. Full
          table returns at md+. */}
      <div className="tw-flex tw-flex-col tw-gap-2 md:tw-hidden">
        {loading && sortedTasks.length === 0 ? (
          <div className="tw-py-5 tw-text-center">
            <Spinner>Loading...</Spinner>
          </div>
        ) : sortedTasks.length === 0 ? (
          <div className="tw-py-6 tw-text-center tw-text-content-muted">
            <i className="bi bi-inbox tw-text-xl tw-block tw-mb-2"></i>
            No project tasks found
          </div>
        ) : (
          sortedTasks.map((task) => {
            const isExpanded = expandedRows.includes(task.id);
            const hasEfforts = (task.efforts?.length ?? 0) > 0;
            return (
              <div
                key={`m-${task.id}`}
                className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-3 tw-shadow-sm"
              >
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                  {onTaskClick ? (
                    <IdLink onClick={() => onTaskClick(task)}>{task.taskNumber || `Task ${task.id}`}</IdLink>
                  ) : (
                    <IdText>{task.taskNumber || <EmptyValue />}</IdText>
                  )}
                  <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
                    <StatusBadge status={task.status} />
                    <TaskActionsDropdown
                      task={task}
                      canEditTask={canEditTask}
                      canCloneTask={canCloneTask}
                      canCloseTask={canCloseTask}
                      canReopenTask={canReopenTask}
                      canCancelTask={canCancelTask}
                      onViewHistory={() => onViewHistory(task)}
                      onEditTask={() => onEditTask(task)}
                      onCloneTask={() => onCloneTask(task)}
                      onCloseTask={() => onCloseTask(task)}
                      onReopenTask={() => onReopenTask(task)}
                      onCancelTask={() => onCancelTask(task)}
                      onPrintPDF={() => onPrintPDF(task)}
                      onConvertToQuantified={
                        onConvertToQuantified ? () => onConvertToQuantified(task) : undefined
                      }
                      onConvertToCustom={onConvertToCustom ? () => onConvertToCustom(task) : undefined}
                    />
                  </div>
                </div>
                <div className="tw-mt-1 tw-break-words tw-font-medium tw-text-content">
                  {task.name || task.description || <EmptyValue />}
                </div>
                <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-text-sm">
                  {task.project && (
                    <span className="tw-text-content-muted">
                      Project{' '}
                      <Link
                        to={`/projects/${task.project.id}`}
                        className="tw-font-medium tw-text-primary tw-no-underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {task.project.projectNumber || task.project.name}
                      </Link>
                    </span>
                  )}
                  <span className="tw-text-content-muted">
                    Type <span className="tw-font-medium tw-text-content">{task.type}</span>
                  </span>
                  {task.activities && task.activities.length > 0 && (
                    <span className="tw-text-content-muted">
                      Coding{' '}
                      <span className="tw-font-medium tw-text-content">{task.activities[0].activityNumber}</span>
                    </span>
                  )}
                </div>
                {hasEfforts && (
                  <>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="tw-mt-3 tw-w-full"
                      active={isExpanded}
                      onClick={() => toggleEfforts(task.id)}
                      title={isExpanded ? 'Hide Efforts' : 'Show Efforts'}
                    >
                      <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} tw-mr-1`} />
                      {isExpanded ? 'Hide efforts' : 'View efforts'}
                    </Button>
                    {isExpanded && (
                      <div className="tw-mt-2 tw-overflow-x-auto">
                        <TaskEffortsTable
                          efforts={task.efforts || []}
                          columns={{ poNumber: true, fuelSurcharge: true }}
                          projectMarkups={projectMarkups(task.project)}
                          canToggleVehicleFuel={canToggleVehicleFuel}
                          onVehicleFuelEffortPatched={onVehicleFuelEffortPatched}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* The ONE scroll box for the table (both axes): the sticky header and the pinned Task ID /
          Actions columns attach to it (W-M20, W-M8). Nothing between it and <table> may have
          overflow. Fills ListPage.Body. */}
      <TableScroll ref={tableWrapperRef} className="tw-hidden md:tw-block">
        {loading && (
          <div
            data-testid="project-works-loading"
            className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-surface/70"
          >
            <Spinner>Loading...</Spinner>
          </div>
        )}
        <Table
          striped
          hover
          stickyHeader
          style={{ tableLayout: 'fixed', minWidth: '100%', width: 'max-content' }}
        >
          <thead>
            <tr>
              {/* V3-025 / ACT-04: the expand column leads the row, reserved on every row. */}
              <th className="wrap-pin-left" style={{ width: EXPAND_COL_WIDTH }} aria-label="Expand" />
              <SortableTh
                label="Task ID"
                sortKey="taskNumber"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                className="wrap-pin-left wrap-pin-edge"
                style={{ width: getColumnWidth('taskNumber'), '--wrap-pin-left': EXPAND_COL_WIDTH } as React.CSSProperties}
              >
                <ResizeHandle
                  columnId="taskNumber"
                  currentWidthPx={getColumnWidthPx('taskNumber')}
                  onResizeStart={startResize}
                />
              </SortableTh>
              <SortableTh
                label="Name"
                sortKey="name"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                style={{ width: getColumnWidth('name') }}
              >
                <ResizeHandle
                  columnId="name"
                  currentWidthPx={getColumnWidthPx('name')}
                  onResizeStart={startResize}
                />
              </SortableTh>
              <SortableTh
                label="Project"
                sortKey="project"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                style={{ width: getColumnWidth('project') }}
              >
                <ResizeHandle
                  columnId="project"
                  currentWidthPx={getColumnWidthPx('project')}
                  onResizeStart={startResize}
                />
              </SortableTh>
              <SortableTh
                label="Status"
                sortKey="status"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                style={{ width: getColumnWidth('status') }}
                className="tw-text-center"
              >
                <ResizeHandle
                  columnId="status"
                  currentWidthPx={getColumnWidthPx('status')}
                  onResizeStart={startResize}
                />
              </SortableTh>
              <SortableTh
                label="Type"
                sortKey="type"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                style={{ width: getColumnWidth('type') }}
                className="tw-text-center"
              >
                <ResizeHandle
                  columnId="type"
                  currentWidthPx={getColumnWidthPx('type')}
                  onResizeStart={startResize}
                />
              </SortableTh>
              <th style={{ width: getColumnWidth('coding') }} className="tw-text-center">
                <InfoTooltip
                  content={
                    <div>
                      <div>
                        <strong>Coding Formula:</strong>
                      </div>
                      <div style={{ marginTop: '4px' }}>
                        If a task belongs to a project that has a job number, the task is coded to the job
                        number. Otherwise, the task is coded to its assigned activity number.
                      </div>
                    </div>
                  }
                  id="coding-tooltip-project-works"
                  placement="top"
                  maxWidth="400px"
                >
                  <span>
                    Coding <i className="bi bi-info-circle tw-ml-1" style={{ fontSize: '0.85rem', opacity: 0.7 }} />
                  </span>
                </InfoTooltip>
                <ResizeHandle
                  columnId="coding"
                  currentWidthPx={getColumnWidthPx('coding')}
                  onResizeStart={startResize}
                />
              </th>
              <th style={{ width: getColumnWidth('activity') }} className="tw-text-center">
                Activity
                <ResizeHandle
                  columnId="activity"
                  currentWidthPx={getColumnWidthPx('activity')}
                  onResizeStart={startResize}
                />
              </th>
              {/* Pinned right and sized to the kebab so it never overlaps the columns beside it
                  (W-M8). Not resizable. */}
              <th style={{ width: PROJECT_WORKS_ACTIONS_COL_WIDTH }} className="wrap-pin-right tw-text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTasks.map((task) => (
              <React.Fragment key={task.id}>
                <tr
                  className={`tw-align-middle${onTaskClick ? ' table-row-clickable' : ''}`}
                  onClick={onTaskClick ? (e) => handleRowClick(task, e) : undefined}
                  style={onTaskClick ? { cursor: 'pointer' } : undefined}
                >
                  <td className="wrap-pin-left" style={{ width: EXPAND_COL_WIDTH }}>
                    {task.efforts && task.efforts.length > 0 && (
                      <RowExpandToggle
                        expanded={expandedRows.includes(task.id)}
                        onToggle={() => toggleEfforts(task.id)}
                        label={expandedRows.includes(task.id) ? 'Hide efforts' : 'Show efforts'}
                      />
                    )}
                  </td>
                  <td
                    className="wrap-pin-left wrap-pin-edge"
                    style={{ '--wrap-pin-left': EXPAND_COL_WIDTH } as React.CSSProperties}
                  >
                    {onTaskClick ? (
                      // Task ID opens the task: the shared blue ID link, not a dark pill (W-M4).
                      <IdLink onClick={() => onTaskClick(task)}>{task.taskNumber || `Task ${task.id}`}</IdLink>
                    ) : (
                      <IdText>{task.taskNumber || <EmptyValue />}</IdText>
                    )}
                  </td>
                  <td>
                    {/* 2-line clamp at the real cell edge with the full text in a tooltip (W-iP11). */}
                    {task.name || task.description ? (
                      <ClampText>{task.name || task.description}</ClampText>
                    ) : (
                      <EmptyValue />
                    )}
                  </td>
                  <td>
                    {task.project ? (
                      // V3-022 / TBL-06: two lines at most - the project number (the link) and the
                      // name clamped to one line. The name sits outside the link so a tap opens its
                      // shared tooltip instead of navigating (TCH-02, TCH-03).
                      <div className="tw-min-w-0">
                        <Link
                          to={`/projects/${task.project.id}`}
                          className="tw-text-xs tw-font-semibold tw-text-primary tw-no-underline hover:tw-underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {task.project.projectNumber || task.project.id}
                        </Link>
                        {task.project.name && (
                          <ClampText lines={1} as="div" className="tw-text-sm tw-text-content-muted">
                            {task.project.name}
                          </ClampText>
                        )}
                      </div>
                    ) : (
                      <EmptyValue />
                    )}
                  </td>
                  <td className="tw-text-center">
                    <StatusBadge status={task.status} />
                  </td>
                  <td className="tw-text-center">
                    <Badge pill bg={task.type === 'Quantified' ? 'info' : task.type === 'Routine' ? 'warning' : 'secondary'}>{task.type}</Badge>
                  </td>
                  <td className="tw-text-center">
                    {task.project?.jobNumber ? (
                      <IdText>{task.project.jobNumber}</IdText>
                    ) : task.activities && task.activities.length > 0 ? (
                      <IdText>{task.activities[0].activityNumber}</IdText>
                    ) : (
                      <EmptyValue />
                    )}
                  </td>
                  <td className="tw-text-center">
                    {task.activities && task.activities.length > 0 ? (
                      <div className="tw-flex tw-flex-col tw-gap-1">
                        {task.activities.map((activity, index) => (
                          <span key={activity.id || index} className="tw-text-sm">
                            {activity.activityNumber} - {activity.activityName}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <EmptyValue />
                    )}
                  </td>
                  <td className="wrap-pin-right tw-text-right">
                    <div className="tw-flex tw-gap-2 tw-justify-end tw-items-center">
                      <TaskActionsDropdown
                        task={task}
                        canEditTask={canEditTask}
                        canCloneTask={canCloneTask}
                        canCloseTask={canCloseTask}
                        canReopenTask={canReopenTask}
                        canCancelTask={canCancelTask}
                        onViewHistory={() => onViewHistory(task)}
                        onEditTask={() => onEditTask(task)}
                        onCloneTask={() => onCloneTask(task)}
                        onCloseTask={() => onCloseTask(task)}
                        onReopenTask={() => onReopenTask(task)}
                        onCancelTask={() => onCancelTask(task)}
                        onPrintPDF={() => onPrintPDF(task)}
                        onConvertToQuantified={
                          onConvertToQuantified ? () => onConvertToQuantified(task) : undefined
                        }
                        onConvertToCustom={onConvertToCustom ? () => onConvertToCustom(task) : undefined}
                        onReportAccomplishment={
                          onReportAccomplishment ? () => onReportAccomplishment(task) : undefined
                        }
                        onSetInternalReview={
                          onSetInternalReview ? () => onSetInternalReview(task) : undefined
                        }
                        onSetActive={onSetActive ? () => onSetActive(task) : undefined}
                      />
                    </div>
                  </td>
                </tr>
                {expandedRows.includes(task.id) && (
                  <tr>
                    <td colSpan={9} className="tw-p-0">
                      <div className="tw-border-t-0 tw-p-3 tw-bg-surface-alt">
                        <TaskEffortsTable
                          efforts={task.efforts || []}
                          columns={{ poNumber: true, fuelSurcharge: true }}
                          projectMarkups={projectMarkups(task.project)}
                          canToggleVehicleFuel={canToggleVehicleFuel}
                          onVehicleFuelEffortPatched={onVehicleFuelEffortPatched}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {sortedTasks.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="tw-text-center tw-text-content-muted tw-py-4">
                  <i className="bi bi-inbox tw-text-xl tw-block tw-mb-2"></i>
                  No project tasks found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableScroll>
    </div>
  );
};

export default ProjectWorksTable;
